import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu, X, Home, BookOpen, Image, ShoppingBag, Users, Phone, Heart,
  LayoutDashboard, Shield, Settings, Scale
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const SITE_LINKS = [
  { name: 'Accueil', href: '/home', icon: Home },
  { name: 'Blog/Tracts', href: '/blog', icon: BookOpen },
  { name: 'Galerie', href: '/gallery', icon: Image },
  { name: 'Boutique', href: '/shop', icon: ShoppingBag },
  { name: 'Adhésion', href: '/membership', icon: Users },
  { name: 'Équipe', href: '/equipe', icon: Users },
  { name: 'Dons', href: '/donate', icon: Heart },
  { name: 'Contact', href: '/contact', icon: Phone },
];

const DASHBOARD_LINKS = {
  fondateur: [
    { name: 'Dashboard Fondateur', href: '/dashboard/fondateur', icon: LayoutDashboard },
    { name: 'Sécurité & Connexions', href: '/dashboard/fondateur/security', icon: Shield },
    { name: 'Conformité juridique', href: '/dashboard/fondateur/conformite', icon: Scale },
    { name: 'Sécurité (2FA)', href: '/dashboard/fondateur/2fa', icon: Shield },
    { name: 'Administration système', href: '/dashboard/fondateur/system', icon: Settings },
  ],
  'co-fondateur': [
    { name: 'Dashboard Co-fondateur', href: '/dashboard/co-fondateur', icon: LayoutDashboard },
  ],
  membre: [
    { name: 'Dashboard Membre', href: '/dashboard/membre', icon: LayoutDashboard },
  ],
  invite: [
    { name: 'Dashboard Invité', href: '/dashboard/invite', icon: LayoutDashboard },
  ],
};

/**
 * Bouton flottant persistant permettant de sauter directement vers n'importe quelle
 * section du site (ou son tableau de bord) sans avoir à revenir à l'accueil.
 */
const QuickNavMenu = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dashboardLinks = user ? (DASHBOARD_LINKS[user.user_type] || []) : [];

  return (
    <div ref={menuRef} className="fixed bottom-6 right-6 z-[60]" data-testid="quick-nav-menu">
      {open && (
        <div className="absolute bottom-16 right-0 w-64 max-h-[70vh] overflow-y-auto bg-card border border-border rounded-xl shadow-xl p-2 animate-in fade-in slide-in-from-bottom-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground px-2 pt-1 pb-2">Aller à...</p>
          {SITE_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors"
              >
                <Icon className="h-4 w-4 text-primary" />
                {link.name}
              </Link>
            );
          })}
          {dashboardLinks.length > 0 && (
            <>
              <div className="border-t border-border my-2" />
              <p className="text-xs uppercase tracking-wide text-muted-foreground px-2 pb-2">Mon espace</p>
              {dashboardLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors"
                  >
                    <Icon className="h-4 w-4 text-secondary" />
                    {link.name}
                  </Link>
                );
              })}
            </>
          )}
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-primary/90 hover:scale-105 transition-all duration-200"
        aria-label="Ouvrir le menu de navigation rapide"
        title="Navigation rapide"
        data-testid="quick-nav-toggle"
      >
        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>
    </div>
  );
};

export default QuickNavMenu;
